import { css } from "@emotion/react";
import CloseIcon from "@mui/icons-material/Close";
import { Dialog, DialogContent, IconButton, useTheme } from "@mui/material";
import { MouseEventHandler, useState } from "react";
import { match } from "ts-pattern";

import Loading from "~/components/me/bento/Loading";
import RetriableError from "~/components/me/bento/RetriableError";
import ContentBodyLayout from "~/components/me/bento/content/ContentBodyLayout";
import { useRadioResponseMemoryContent } from "~/components/me/bento/lib";
import { useOpenContentModal } from "~/components/me/hooks";
import { ContentName } from "~/components/me/lib";
import { wrapLongTextStyle } from "~/lib/client/design";
import { engFont } from "~/lib/client/fonts";
import { MeRadioResponseMemoryContentQuery, Sdk } from "~/lib/graphql/client";
import { formatDateTime } from "~/lib/graphql/graphqlClient";

const contentName: ContentName = "RadioResponseMemory";

const RadioResponseMemory = ({ client }: { client: Sdk }) => {
  const result = useRadioResponseMemoryContent(client);
  const theme = useTheme();

  const { openedContent, openContentModal, closeContentModal } =
    useOpenContentModal();
  const isOpen = openedContent === contentName && result.status === "success";
  const openModal = () => {
    if (result.status !== "success") return;
    openContentModal(contentName);
  };

  const closeModal = () => closeContentModal();

  return (
    <>
      {match(result)
        .with({ status: "pending" }, () => <Loading />)
        .with({ status: "error" }, (matched) => (
          <RetriableError reload={matched.refetch} />
        ))
        .with({ status: "success" }, (matched) => (
          <Body content={matched.data} onClick={openModal} />
        ))
        .exhaustive()}
      {result.status == "success" && (
        <Dialog
          open={isOpen}
          onClose={closeModal}
          fullWidth
          PaperProps={{
            sx: {
              width: "100%",
              height: "80vh",
              m: 1,
              backgroundColor: theme.palette.background.paper,
            },
          }}
          maxWidth={"md"}
        >
          <DialogContent>
            <IconButton onClick={closeModal}>
              <CloseIcon sx={{ color: theme.palette.text.primary }} />
            </IconButton>
            <DialogBody content={result.data} />
          </DialogContent>
        </Dialog>
      )}
    </>
  );
};

const Body = ({
  content,
  onClick,
}: {
  content: MeRadioResponseMemoryContentQuery;
  onClick: MouseEventHandler;
}) => {
  const contentSize = content.me.radioResponses.length;
  const theme = useTheme();

  return (
    <ContentBodyLayout onClick={onClick} label={"Radio Memory"}>
      <div css={rootStyle}>
        <p
          className={engFont.className}
          css={css`
            font-size: 80px;
            font-weight: 700;
            color: ${theme.palette.secondary.main};
            margin: 0;
          `}
        >
          {contentSize}
        </p>
      </div>
    </ContentBodyLayout>
  );
};

const DialogBody = ({
  content,
}: {
  content: MeRadioResponseMemoryContentQuery;
}) => {
  const segments = content.me.radioResponses
    .flatMap((r) => ({
      segment: r.radioSegment,
      channel: r.radioSegment.radioChannel,
    }))
    .reduce(
      (acc, cur) => {
        if (acc.map((r) => r.segment.id).includes(cur.segment.id)) {
          return acc;
        }
        acc.push(cur);
        return acc;
      },
      [] as {
        segment: MeRadioResponseMemoryContentQuery["me"]["radioResponses"][number]["radioSegment"];
        channel: MeRadioResponseMemoryContentQuery["me"]["radioResponses"][number]["radioSegment"]["radioChannel"];
      }[]
    )
    .sort((a, b) => {
      const aSize = content.me.radioResponses.filter(
        (r) => r.radioSegment.id === a.segment.id
      ).length;
      const bSize = content.me.radioResponses.filter(
        (r) => r.radioSegment.id === b.segment.id
      ).length;
      return bSize - aSize;
    });

  return (
    <div>
      {segments.map((s) => (
        <SegmentSection
          segment={s.segment}
          channel={s.channel}
          responses={content.me.radioResponses}
          key={s.segment.id}
        />
      ))}
    </div>
  );
};

const SegmentSection = ({
  segment,
  channel,
  responses,
}: {
  segment: MeRadioResponseMemoryContentQuery["me"]["radioResponses"][number]["radioSegment"];
  channel: MeRadioResponseMemoryContentQuery["me"]["radioResponses"][number]["radioSegment"]["radioChannel"];
  responses: MeRadioResponseMemoryContentQuery["me"]["radioResponses"];
}) => {
  const [isOpened, setIsOpened] = useState(false);
  const segmentResponseCount = responses.filter(
    (r) => r.radioSegment.id === segment.id
  ).length;
  const theme = useTheme();
  return (
    <div
      css={css`
        margin: 1rem;
        background: white;
      `}
    >
      <h3
        css={css`
          cursor: pointer;
          // open時上部固定
          position: sticky;
          top: -20px;
          z-index: 1;
          background-color: white;
          padding: 1rem;
          margin: 0;
        `}
        role="button"
        onClick={() => setIsOpened(!isOpened)}
      >
        {segment.name} / {channel.title} (
        {segmentResponseCount.toLocaleString()}件)
      </h3>
      {isOpened && (
        <ul
          css={css`
            padding: 1rem;
            margin: 0;
          `}
        >
          {responses
            .filter((r) => r.radioSegment.id === segment.id)
            .compact()
            .sort((a, b) => {
              return a.createdAt < b.createdAt ? 1 : -1;
            })
            .map((r) => (
              <li
                key={r.id}
                css={css`
                  background: ${theme.palette.background.paper};
                  padding: 1rem;
                  border-radius: 0.25rem;
                  list-style-type: none;
                  margin-bottom: 1rem;
                `}
              >
                <p
                  css={css`
                    ${wrapLongTextStyle};
                  `}
                >
                  {r.content}
                </p>
                <div
                  css={css`
                    // 横線
                    width: 100%;
                    height: 1px;
                    background: #333;
                    margin: 1rem 0;
                  `}
                />
                <div
                  css={css`
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                  `}
                >
                  <p>{formatDateTime(r.createdAt)}</p>
                  {r.isAdopted && <p>採用されました！</p>}
                </div>
              </li>
            ))}
        </ul>
      )}
    </div>
  );
};

const rootStyle = css`
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;

  width: 100%;
  height: 108%;
  padding: 1rem 2rem 0;
  margin-top: -1rem;
  margin-bottom: 1rem;
`;

export default RadioResponseMemory;
